import type * as A from '@/api';
import type { Filter, Sorting } from '@/models/objects/datasetViewState';
import { logger } from '@/utils/logger';
import { graphql } from '@/utils/graphql';
import { noLint_waitForUnloadToComplete } from '@/woLint/services/download';
import User from './user';
import DatasetTest from './datasetTest';

export type SupportedDownloadRecordTypes =
  | 'dataObject'
  | 'dataset'
  | 'datasetTest'
  | 'datasetAllTests';

export type SupportedDownloadFileTypes = 'csv' | 'xlsx';

type DownloadConstructorInput = A.GetDownloadQuery['getDownload'];

export default class Download {
  readonly id: string;
  readonly dataId: string;
  readonly dataTimeStamp: number;
  readonly requester: User;
  readonly requestTimeStamp: number;
  readonly signedDownloadUrl: string;

  private constructor(input: DownloadConstructorInput) {
    this.id = input.id;
    this.dataId = input.dataId;
    this.dataTimeStamp = input.dataTimeStamp;
    this.requester = new User(input.requester);
    this.requestTimeStamp = input.requestTimeStamp;
    this.signedDownloadUrl = input.signedDownloadUrl;
  }

  static async getDownloadsByDataId(dataId: string): Promise<Download[]> {
    logger.info({ label: 'app.model.download.getDownloadsByDataId.dataId', message: dataId });
    const data = await graphql('listDownloadsByData', { dataId });
    return data.items.map((el) => new Download(el));
  }

  /**
   * Async unload — Step 1: start the unload process.
   * Returns executionId + checkUnloadProps to pass to subsequent calls.
   */
  private static async startUnload(
    dataType: SupportedDownloadRecordTypes,
    dataId: string,
    dataName: string,
    dataCloudResourceId: string,
    version: number,
    fileType: SupportedDownloadFileTypes = 'csv',
    filters: Filter[] = [],
    sortings: Sorting[] = [],
  ): Promise<{ executionId: string; checkUnloadProps: string }> {
    logger.info(dataType, { label: 'app.service.download.startUnload.dataType' });
    logger.info(dataId, { label: 'app.service.download.startUnload.dataId' });
    logger.info(dataCloudResourceId, {
      label: 'app.service.download.startUnload.dataCloudResourceId',
    });

    let lookup: Record<string, string> | undefined;
    if (dataType === 'datasetAllTests') {
      const tests = await DatasetTest.getDatasetTestsByDataset(dataId);
      lookup = tests.reduce<Record<string, string>>((acc, cur) => {
        acc[cur.id] = cur.name;
        return acc;
      }, {});
    }

    const query: A.StartUnloadQueryInput = {
      dataType,
      dataCloudResourceId,
      fileType,
      dataName,
      mapTestUuidToName: lookup ? JSON.stringify(lookup) : undefined,
      filters: JSON.stringify(filters.filter((f) => f.active)),
      sortings: JSON.stringify(sortings),
    };

    if (version > 0) {
      query.version = String(version);
    }

    const resp = await graphql('beemAgentStartUnload', { query });
    logger.info(resp.executionId, { label: 'app.service.download.startUnload.executionId' });
    return { executionId: resp.executionId, checkUnloadProps: resp.checkUnloadProps };
  }

  /**
   * Async unload — Step 2: poll until unload completes.
   * Calls checkUnload repeatedly until isStillRunning is false.
   */
  private static async waitForUnloadToComplete(
    dataType: SupportedDownloadRecordTypes,
    dataCloudResourceId: string,
    executionId: string,
    checkUnloadProps: string,
  ): Promise<void> {
    return noLint_waitForUnloadToComplete(
      dataType,
      dataCloudResourceId,
      executionId,
      checkUnloadProps,
    );
  }

  /**
   * Async unload — Step 3: finalize and persist the Download record to DynamoDB.
   * Called once the unload is confirmed done. Returns the Download with signedDownloadUrl.
   */
  private static async finishUnload(
    dataType: SupportedDownloadRecordTypes,
    dataId: string,
    dataCloudResourceId: string,
    executionId: string,
    checkUnloadProps: string,
  ): Promise<Download> {
    const input: A.FinishUnloadInput = {
      dataType,
      dataId,
      dataCloudResourceId,
      executionId,
      checkUnloadProps,
    };

    const resp = await graphql('beemAgentFinishUnload', { input });
    return new Download(resp);
  }

  /**
   * Full async unload flow: start → poll → finish.
   * Drop-in replacement for the old synchronous invokeDownload.
   */
  static async invokeDownload(
    dataType: SupportedDownloadRecordTypes,
    dataId: string,
    dataName: string,
    dataCloudResourceId: string,
    version: number,
    fileType: SupportedDownloadFileTypes = 'csv',
    filters: Filter[] = [],
    sortings: Sorting[] = [],
  ): Promise<Download> {
    // Step 1: Start
    const { executionId, checkUnloadProps } = await Download.startUnload(
      dataType,
      dataId,
      dataName,
      dataCloudResourceId,
      version,
      fileType,
      filters,
      sortings,
    );

    // Step 2: Poll until done
    await Download.waitForUnloadToComplete(
      dataType,
      dataCloudResourceId,
      executionId,
      checkUnloadProps,
    );

    // Step 3: Finalize and persist
    return Download.finishUnload(
      dataType,
      dataId,
      dataCloudResourceId,
      executionId,
      checkUnloadProps,
    );
  }
}
